/**
 * Skills Merger Utility
 * Handles detection and merging of duplicate skills based on case-insensitive matching
 * and similar skill name patterns
 */

/**
 * Safely extract skill name from various formats
 */
export const getSafeSkillName = (skill) => {
  if (typeof skill === 'string') {
    return skill.replace(/^"|"$/g, '').trim();
  }
  if (typeof skill === 'object' && skill !== null) {
    return (skill.name || skill.skill || skill.title || '').toString().trim();
  }
  return '';
};

/**
 * Normalize skill name for comparison
 */
export const normalizeSkillName = (skillName) => {
  return getSafeSkillName(skillName)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s]/g, '') // Remove special characters except spaces
    .trim();
};

/**
 * Check if two skills are similar enough to be considered duplicates
 */
export const areSkillsSimilar = (skill1, skill2, threshold = 0.8) => {
  const name1 = normalizeSkillName(skill1);
  const name2 = normalizeSkillName(skill2);
  
  // Exact match
  if (name1 === name2) return true;
  
  // Check for common abbreviations or variations
  const commonVariations = {
    'seo': 'search engine optimization',
    'sem': 'search engine marketing',
    'ui': 'user interface',
    'ux': 'user experience',
    'js': 'javascript',
    'css': 'cascading style sheets',
    'html': 'hypertext markup language',
    'api': 'application programming interface',
    'crm': 'customer relationship management',
    'cms': 'content management system',
    'hr': 'human resources',
    'it': 'information technology',
    'qa': 'quality assurance',
    'ai': 'artificial intelligence',
    'ml': 'machine learning'
  };
  
  // Check if one is an abbreviation of the other
  for (const [abbrev, full] of Object.entries(commonVariations)) {
    if ((name1 === abbrev && name2.includes(full)) || 
        (name2 === abbrev && name1.includes(full))) {
      return true;
    }
  }
  
  // Check for substring matches (one skill name contains the other)
  if (name1.length >= 3 && name2.length >= 3) {
    if (name1.includes(name2) || name2.includes(name1)) {
      return true;
    }
  }
  
  // Levenshtein distance check for similar names
  const distance = calculateLevenshteinDistance(name1, name2);
  const maxLength = Math.max(name1.length, name2.length);
  const similarity = 1 - (distance / maxLength);
  
  return similarity >= threshold;
};

/**
 * Calculate Levenshtein distance between two strings
 */
const calculateLevenshteinDistance = (str1, str2) => {
  const matrix = Array(str2.length + 1).fill().map(() => Array(str1.length + 1).fill(0));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j - 1][i] + 1,     // deletion
        matrix[j][i - 1] + 1,     // insertion
        matrix[j - 1][i - 1] + cost // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

/**
 * Find duplicate skills within an array of skills
 */
export const findDuplicateSkills = (skills) => {
  const duplicates = [];
  const processed = new Set();
  
  for (let i = 0; i < skills.length; i++) {
    if (processed.has(i)) continue;
    
    const currentSkill = skills[i];
    const currentName = getSafeSkillName(currentSkill);
    
    if (!currentName) continue;
    
    const duplicateGroup = [{ index: i, skill: currentSkill }];
    
    // Find all similar skills
    for (let j = i + 1; j < skills.length; j++) {
      if (processed.has(j)) continue;
      
      const compareSkill = skills[j];
      const compareName = getSafeSkillName(compareSkill);
      
      if (!compareName) continue;
      
      if (areSkillsSimilar(currentName, compareName)) {
        duplicateGroup.push({ index: j, skill: compareSkill });
        processed.add(j);
      }
    }
    
    if (duplicateGroup.length > 1) {
      duplicates.push(duplicateGroup);
    }
    
    processed.add(i);
  }
  
  return duplicates;
};

/**
 * Merge duplicate skills, keeping the best data from each
 */
export const mergeDuplicateSkills = (duplicateGroup) => {
  if (!duplicateGroup || duplicateGroup.length <= 1) {
    return duplicateGroup[0]?.skill || null;
  }
  
  // Sort by priority: higher rating > more experience > more complete data
  const sortedSkills = duplicateGroup.sort((a, b) => {
    const skillA = a.skill;
    const skillB = b.skill;
    
    // Prioritize object format over string format
    const formatScoreA = typeof skillA === 'object' ? 10 : 0;
    const formatScoreB = typeof skillB === 'object' ? 10 : 0;
    
    if (formatScoreA !== formatScoreB) {
      return formatScoreB - formatScoreA;
    }
    
    if (typeof skillA === 'object' && typeof skillB === 'object') {
      // Compare ratings
      const ratingA = skillA.rating || 0;
      const ratingB = skillB.rating || 0;
      if (ratingA !== ratingB) return ratingB - ratingA;
      
      // Compare experience length
      const expA = (skillA.experience || '').length;
      const expB = (skillB.experience || '').length;
      if (expA !== expB) return expB - expA;
      
      // Compare completeness (number of filled fields)
      const completenessA = Object.keys(skillA).filter(k => skillA[k] && skillA[k] !== '').length;
      const completenessB = Object.keys(skillB).filter(k => skillB[k] && skillB[k] !== '').length;
      return completenessB - completenessA;
    }
    
    return 0;
  });
  
  const primarySkill = sortedSkills[0].skill;
  
  // If primary skill is an object, merge additional data from other skills
  if (typeof primarySkill === 'object') {
    const mergedSkill = { ...primarySkill };
    
    // Merge experience from other skills if primary doesn't have enough
    if ((!mergedSkill.experience || mergedSkill.experience.length < 50)) {
      for (const { skill } of sortedSkills.slice(1)) {
        if (typeof skill === 'object' && skill.experience && skill.experience.length > (mergedSkill.experience || '').length) {
          mergedSkill.experience = skill.experience;
          break;
        }
      }
    }
    
    // Use the best skill name (usually the most properly capitalized one)
    const skillNames = sortedSkills.map(s => getSafeSkillName(s.skill)).filter(Boolean);
    const bestName = skillNames.find(name => 
      name.split(' ').every(word => word[0] === word[0].toUpperCase())
    ) || skillNames[0];
    
    if (bestName) {
      mergedSkill.name = bestName;
    }
    
    // Ensure we have a unique ID
    if (!mergedSkill.id) {
      mergedSkill.id = `merged-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    }
    
    return mergedSkill;
  }
  
  // For string format, return the best formatted version
  const skillNames = sortedSkills.map(s => getSafeSkillName(s.skill)).filter(Boolean);
  return skillNames.find(name => 
    name.split(' ').every(word => word[0] === word[0].toUpperCase())
  ) || skillNames[0];
};

/**
 * Process and merge duplicate skills in an array
 */
export const processAndMergeDuplicates = (skills) => {
  if (!Array.isArray(skills) || skills.length === 0) {
    return { skills: [], duplicatesFound: 0, duplicatesMerged: 0 };
  }
  
  const duplicateGroups = findDuplicateSkills(skills);
  
  if (duplicateGroups.length === 0) {
    return { skills: [...skills], duplicatesFound: 0, duplicatesMerged: 0 };
  }
  
  const mergedSkills = [];
  const processedIndices = new Set();
  let duplicatesMerged = 0;
  
  // Process duplicate groups
  for (const group of duplicateGroups) {
    const mergedSkill = mergeDuplicateSkills(group);
    if (mergedSkill) {
      mergedSkills.push(mergedSkill);
      duplicatesMerged += group.length - 1; // Count merged skills
    }
    
    // Mark all indices in this group as processed
    group.forEach(({ index }) => processedIndices.add(index));
  }
  
  // Add non-duplicate skills
  for (let i = 0; i < skills.length; i++) {
    if (!processedIndices.has(i)) {
      mergedSkills.push(skills[i]);
    }
  }
  
  return {
    skills: mergedSkills,
    duplicatesFound: duplicateGroups.length,
    duplicatesMerged,
    totalSkillsBefore: skills.length,
    totalSkillsAfter: mergedSkills.length
  };
};

/**
 * Get statistics about potential duplicates in skills array
 */
export const getDuplicateStats = (skills) => {
  const duplicateGroups = findDuplicateSkills(skills);
  const stats = {
    totalSkills: skills.length,
    duplicateGroups: duplicateGroups.length,
    duplicateSkills: duplicateGroups.reduce((sum, group) => sum + group.length, 0),
    uniqueSkills: skills.length - duplicateGroups.reduce((sum, group) => sum + (group.length - 1), 0),
    potentialSavings: duplicateGroups.reduce((sum, group) => sum + (group.length - 1), 0)
  };
  
  return stats;
};